import * as yup from "yup";
import { array, boolean, date, mixed, number, object, string } from "yup";

export enum OrganizationRole {
  ADMIN = "ADMIN",
}

export enum EntitlementType {
  DEVICE_LIMIT = "DEVICE_LIMIT",
  USER_LIMIT = "USER_LIMIT",
  CONSULTING = "CONSULTING",
  ALL_DOCUMENTS_UNLOCKED = "ALL_DOCUMENTS_UNLOCKED",
  PREMIUM_MODELS = "PREMIUM_MODELS",
  ADMINISTER_ALL_TRAININGS = "ADMINISTER_ALL_TRAININGS",
}

export enum BillingInterval {
  MONTHLY = "month",
  YEARLY = "year",
}

export type SimpleSubscription = {
  products: string[];
  billingInterval: BillingInterval;
  quantity: number;
  entitlements: Entitlement[];
};

export const EntitlementSchema = object({
  id: string().uuid().required(),
  type: mixed<EntitlementType>()
    .oneOf(Object.values(EntitlementType))
    .required(),
  value_bool: boolean().nullable(),
  value_int: number().integer().nullable(),
});

export const ProductSchema = object({
  id: string().uuid().required(),
  name: string().required(),
  stripeProductId: string().required(),
  entitlements: array(EntitlementSchema),
});

export const SubscriptionItemSchema = object({
  id: string().uuid().required(),
  subscriptionId: string().uuid().required(),
  product: ProductSchema.required(),
  productId: string().uuid().required(),
  productQuantity: number().integer().nullable(),
  interval: string().oneOf(Object.values(BillingInterval)).required(),
});

export const SubscriptionSchema = object({
  id: string().uuid().required(),
  organizationId: string().uuid().required(),
  status: string().required(),
  stripeSubscriptionId: string().required(),
  subscriptionItems: array(SubscriptionItemSchema).required(),
});

export const OrganizationSchema = object({
  id: string().uuid().required(),
  name: string().required(),
  companyDescription: string().nullable().optional(),
  createdAt: date().required(),
  updatedAt: date().required(),
  subscription: SubscriptionSchema.nullable(),
  stripeCustomerId: string().nullable(),
});

export const OrganizationMemberSchema = object({
  id: string().uuid().required(),
  userId: string().uuid().required(),
  invitedByUserId: string().uuid().nullable(),
  organizationId: string().uuid().required(),
  activated: boolean().default(true).required(),
  homeOrg: boolean().required(),
  createdAt: date().required(),
  updatedAt: date().required(),
});

export const FunctionalGroupMembershipSchema = object({
  id: string().uuid().required(),
  functionalGroupId: string().uuid().required(),
  organizationMembershipId: string().uuid().required(),
  functionalGroup: object({
    id: string().uuid().required(),
    name: string().required(),
    acronym: string().required(),
    entitlements: array(EntitlementSchema).optional(),
  }).required(),
  createdAt: string().required(),
});

export const OrganizationMemberWithOrganizationSchema =
  OrganizationMemberSchema.concat(
    object({
      organization: OrganizationSchema,
      functionalGroupMemberships: array(FunctionalGroupMembershipSchema).optional(),
    })
  );

export const OrgScopedOrganizationMembershipSchema = OrganizationMemberSchema.concat(
  object({
    functionalGroupMemberships: array(FunctionalGroupMembershipSchema).optional(),
  })
);

export const BaseUserSchema = object({
  id: string().required(),
  email: string().email().required(),
  authId: string().nullable(),
  firstName: string().nullable(),
  lastName: string().nullable(),
  weeklyDigestEnabled: boolean().required(),
  commentMentionsEnabled: boolean().optional(),
  commentRepliesEnabled: boolean().optional(),
  approvalRequestsEnabled: boolean().optional(),
  approvalsCompletedEnabled: boolean().optional(),
  taskAssignmentsEnabled: boolean().optional(),
  taskStatusChangesEnabled: boolean().optional(),
  taskUpdatesEnabled: boolean().optional(),
  createdAt: date().required(),
  updatedAt: date().required(),
});

export interface BaseUser extends yup.InferType<typeof BaseUserSchema> {}

export const OrgScopedBaseUserSchema = BaseUserSchema.concat(
  object({
    organizationMemberships: array(OrgScopedOrganizationMembershipSchema).optional(),
  })
);

export interface OrgScopedBaseUser
  extends yup.InferType<typeof OrgScopedBaseUserSchema> {}

export const FullUserUiStateSchema = object({
  id: string().uuid().required(),
  userId: string().uuid().required(),
  welcomeSeen: boolean().required(),
  supportTooltipSeen: boolean().required(),
  nextTodoCardTooltipSeen: boolean().required(),
  approvalTooltipSeen: boolean().required(),
  demoCompleteModalSeen: boolean().required(),
  createdAt: date().required(),
  updatedAt: date().required(),
});

export interface FullUserUiState
  extends yup.InferType<typeof FullUserUiStateSchema> {}

export type UserUiState = Omit<
  FullUserUiState,
  "id" | "userId" | "updatedAt" | "createdAt"
>;

export type UserUiStateKey = keyof UserUiState;

export type UpdatableUiState = Partial<UserUiState>;

export const UserSchema = object({
  organizationMemberships: array(
    OrganizationMemberWithOrganizationSchema
  ).required(),
  userUiState: FullUserUiStateSchema.required(),
  ecfrPin: object({
    updatedAt: date().required(),
  })
    .nullable()
    .default(null),
  permissions: array(string()).optional(),
}).concat(BaseUserSchema);

// enum SubscriptionStatus {
//   INCOMPLETE = "incomplete",
//   INCOMPLETE_EXPIRED = "incomplete_expired",
//   TRIALING = "trialing",
//   ACTIVE = "active",
//   PAST_DUE = "past_due",
//   CANCELED = "canceled",
//   UNPAID = "unpaid",
//   PAUSED = "paused",
// }

export enum Plan {
  PREMIUM = "PREMIUM",
  PREMIUM_PLUS = "PREMIUM_PLUS",
  FREE = "FREE",
}

// export const SubscriptionSchema = object({
//   status: string()
//     .oneOf(Object.values(SubscriptionStatus))
//     .required()
//     .nullable(),
//   deviceCount: number().required().nullable(),
//   cycle: string().oneOf(Object.values(BillingCycle)).required().nullable(),
//   plan: string().oneOf(Object.values(Plan)).required(),
// });

export interface Organization
  extends yup.InferType<typeof OrganizationSchema> {}

export interface OrganizationMember
  extends yup.InferType<typeof OrganizationMemberSchema> {}

export interface OrganizationMemberWithOrganization
  extends yup.InferType<typeof OrganizationMemberWithOrganizationSchema> {}

export interface OrgScopedOrganizationMembership
  extends yup.InferType<typeof OrgScopedOrganizationMembershipSchema> {}

export interface Entitlement extends yup.InferType<typeof EntitlementSchema> {}
export interface Subscription
  extends yup.InferType<typeof SubscriptionSchema> {}
export interface SubscriptionItem
  extends yup.InferType<typeof SubscriptionItemSchema> {}
export interface Product extends yup.InferType<typeof ProductSchema> {}

export interface Subscription
  extends yup.InferType<typeof SubscriptionSchema> {}

export interface User extends yup.InferType<typeof UserSchema> {}
